"use client";

import { getProfiles } from "@lib/auth/jwt";
import Booking from "@lib/classes/Booking";
import FrameConfig from "@lib/classes/FrameConfig";
import logoutProfile from "@services/logout_profile";
import reprice from "@services/reprice";
import Cookies from "js-cookie";

// Shared across overlapping clearSession() calls — see clearSession below.
let clearSessionInFlight: Promise<void> | null = null;

class Session extends Map {
  static addRoundtrip() {
    const firstLeg = this.getLeg(0);
    const newLeg: be.Reservation = { bagCount: firstLeg.bagCount };
    if (firstLeg.passengers) newLeg.passengers = [...firstLeg.passengers];
    if (firstLeg.pickup) newLeg.dropoff = { ...firstLeg.pickup };
    if (firstLeg.dropoff) newLeg.pickup = { ...firstLeg.dropoff };

    this.setLeg(1, newLeg);
  }

  // Returns true if any leg returns true for the function passed in
  static anyLeg(func: (leg: be.Reservation) => boolean): boolean {
    const booking = Session.getBooking();
    return booking.legs?.some(func);
  }

  // Clear all data entered by user so far.
  // @param clearProfiles - If true, detach non-user profiles from JWT. If keepUserLoggedIn
  //   flag was set on the booking, only the user profile is preserved. Otherwise all
  //   profiles are cleared.
  static async clearBookingData(clearProfiles = false) {
    const keepUserLoggedIn = this.getBooking().flags?.keepUserLoggedIn;
    this.setBooking(new Booking());

    if (clearProfiles) {
      // Detach profiles from JWT based on keepUserLoggedIn flag
      const profiles = getProfiles();
      if (profiles.group) await logoutProfile("GROUP");
      if (profiles.charter) await logoutProfile("CHARTER");
      if (profiles.coupon) await logoutProfile("COUPON");
      // Only logout user if keepUserLoggedIn is NOT set
      if (profiles.user && !keepUserLoggedIn) await logoutProfile("USER");
    }
  }

  // Clear data tied to a specific reservation and generally not reused for other reservations.
  static clearReservationData() {
    const booking = Session.getBooking();
    this.updateBooking({ legs: [], roundtrip: undefined });
    if (booking.otherInfo) booking.otherInfo.termsAndConditions = undefined;
  }

  // The JWT expiry cookie is the one expiry signal touchExpiration() cannot
  // reset, so an expired JWT must silence isExpired() SYNCHRONOUSLY at
  // detection time: clearSession()'s own cookie removal (inside
  // logoutProfile("ALL")) lands only after a network round-trip, and every
  // Session read before it landed re-entered the expiry branch — the production
  // napi request storm pinned in session.test.ts.
  //
  // Called at expiry-DETECTION sites only, deliberately not inside
  // clearSession(): explicit-logout callers (the TopBar handlers, FrameConfig)
  // need the detach mutation to go out with the Bearer token, and Apollo reads
  // that token from the very cookie this removes. Only where the JWT is already
  // expired is removing it first safe — the expiry-path detach then runs
  // TOKENLESS, which napi's detachAllProfiles tolerates: it signs the new token
  // from context.site, and the site comes from the X-Site header Apollo sends on
  // every request, not from the JWT.
  static clearAuthCookies() {
    Cookies.remove(`be_${window.siteId}`);
    Cookies.remove(`be_${window.siteId}_e`);
  }

  // Reset the entire session, including attached profiles. Overlapping calls
  // share the in-flight teardown (see clearSessionInFlight above).
  static clearSession(): Promise<void> {
    if (clearSessionInFlight) return clearSessionInFlight;
    // The guard must be SET before the teardown body runs: the body's own
    // synchronous Session writes (setBooking → set) re-enter clearSession() when
    // the session still reads as expired, and an unassigned guard would let that
    // re-entry start a second teardown. Hence the explicit deferred promise
    // rather than assigning the async IIFE's return value — an async function
    // runs synchronously up to its first await, so its own prefix would re-enter
    // before the assignment landed.
    let settle!: { resolve: () => void; reject: (error: unknown) => void };
    const teardown = new Promise<void>((resolve, reject) => {
      settle = { resolve, reject };
    });
    clearSessionInFlight = teardown;
    (async () => {
      try {
        Session.setBooking(new Booking());
        Session.remove("verifiedBooking");
        await logoutProfile("ALL");
        // apolloClient is optional-chained: teardown can run before the client
        // is published to window. Awaited so a settled teardown really means the
        // GraphQL cache is cleared, and so a failure rejects the shared promise.
        await global.window?.apolloClient?.clearStore(); // Clear GraphQL cache
        new FrameConfig().apply();
        settle.resolve();
      } catch (error) {
        settle.reject(error);
      } finally {
        clearSessionInFlight = null;
      }
    })();
    return teardown;
  }

  // Move active booking to be a "confirmedBooking".
  // Reset the active booking.
  static async confirmBooking() {
    const booking = Session.getBooking();
    Session.set("confirmedBooking", booking);
    Session.setBooking(new Booking());

    // Detach coupon from JWT so it doesn't apply to subsequent bookings
    if (getProfiles().coupon) {
      await logoutProfile("COUPON");
    }

    new FrameConfig().apply();
  }

  // Move active frp purchase to be a "confirmed".
  // Reset the active frp purchase.
  static confirmFrpPurchase() {
    const frpPurchase = Session.get("frpPurchase");
    Session.set("confirmedFrpPurchase", frpPurchase);
    Session.remove("frpPurchase");
  }

  static editVerifiedBooking() {
    const verifiedBooking = Session.getVerifiedBooking();
    if (verifiedBooking) {
      // Make sure service area config matches booking.
      Session.updateConfig({ serviceArea: verifiedBooking.serviceArea });
      Session.setBooking(verifiedBooking);
    }
  }

  static setVerifiedBooking(booking: be.Booking) {
    Session.set("verifiedBooking", booking);
  }

  static defaultLegValues(leg: be.Reservation) {
    leg.passengers = [{ field: "passengers", value: "1" }];
    leg.serviceExtras = [];
    return leg;
  }

  static get(id: string, touchExpiration: boolean = true) {
    if (!global.sessionStorage) return null;
    if (touchExpiration && Session.isExpired()) {
      Session.clearAuthCookies();
      Session.touchExpiration();
      Session.clearSession();
    }
    if (touchExpiration) Session.touchExpiration();

    const value = sessionStorage.getItem(`${window.siteId}.${id}`);
    if (value === null) return null;
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  }

  static remove(id: string) {
    if (!global.sessionStorage) return null;
    sessionStorage.removeItem(`${window.siteId}.${id}`);
  }

  static async removeRoundtrip(dontRecalculate = false) {
    const booking = this.getBooking();
    booking.roundtrip = false;
    booking.legs.splice(1, 1); // Remove second leg
    this.setBooking(booking);

    // Recalculate prices?
    if (!dontRecalculate) {
      const firstLeg = booking.legs[0];
      if (["recalculate", "regenerate"].includes(String(firstLeg.service?.roundtripPricingMode))) {
        await reprice(0, firstLeg.service?.roundtripPricingMode == "recalculate");
      }
    }
  }

  static set(id: string, value: string | object, touchExpiration: boolean = true) {
    if (!global.sessionStorage) return null;
    if (typeof value === "object") value = JSON.stringify(value);

    if (touchExpiration && Session.isExpired()) {
      Session.clearAuthCookies();
      Session.touchExpiration();
      Session.clearSession();
    }

    sessionStorage.setItem(`${window.siteId}.${id}`, value);
    if (touchExpiration) Session.touchExpiration();
  }

  static getBooking(): be.Booking {
    let booking = Session.get("booking");
    if (!booking) booking = new Booking();
    else booking = new Booking(booking);
    return booking;
  }

  static getConfig(): be.GlobalConfig {
    let config = Session.get("config");
    if (!config) config = {};
    return config;
  }

  static getConfirmedBooking(): be.Booking | undefined {
    let booking = Session.get("confirmedBooking");
    if (booking) booking = new Booking(booking);
    return booking;
  }

  static getConfirmedFrpPurchase(): be.FrequentRiderProgramSessionData | undefined {
    let purchase = Session.get("confirmedFrpPurchase");
    return purchase;
  }

  static getCurrentLeg(): be.Reservation | undefined {
    if (!global.window) return undefined;
    return Session.getLeg(window.legIndex);
  }

  static getCurrentLegIndex(): number {
    return window.legIndex;
  }

  static getVerifiedBooking(): be.Booking | undefined {
    let booking = Session.get("verifiedBooking");
    if (booking) booking = new Booking(booking);
    return booking;
  }

  static getLeg(legNum: number): be.Reservation {
    let leg = Session.getBooking().legs?.[legNum];
    if (!leg) leg = Session.defaultLegValues({});
    return leg;
  }

  static isExpired(minutesLeeway = 0): boolean {
    // Session expiration
    let expiration = Session.get("expiration", false);
    if (!expiration) return false;
    let expirationDate = new Date(expiration);
    let compareDate = new Date();
    compareDate.setMinutes(compareDate.getMinutes() + minutesLeeway);
    if (expirationDate.getTime() < compareDate.getTime()) return true;

    // JWT expiration
    expiration = Cookies.get(`be_${window.siteId}_e`);
    if (expiration) {
      expirationDate = new Date(expiration);
      compareDate = new Date();
      compareDate.setMinutes(compareDate.getMinutes() + minutesLeeway);
      if (expirationDate.getTime() < compareDate.getTime()) return true;
    }

    return false;
  }

  static isSecondLeg() {
    if (!global.sessionStorage) return null;
    return window.legIndex == 1;
  }

  static setBooking(booking: be.Booking): void {
    Session.set("booking", booking);
  }

  static setConfig(config: be.GlobalConfig): void {
    Session.set("config", config);
  }

  static setCurrentLeg(leg: be.Reservation): void {
    Session.setLeg(window.legIndex, leg);
  }

  static setLeg(legNum: number, leg: be.Reservation): void {
    const booking = Session.getBooking();
    booking.legs = booking.legs || [];
    booking.legs[legNum] = leg || {};
    Session.updateBooking({ legs: booking.legs });
  }

  // Session expiration is 2 days (rolling).
  static touchExpiration() {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 2);
    Session.set("expiration", expirationDate, false);
  }

  static updateBooking(values: Partial<be.Booking>): void {
    let oldBooking = Session.getBooking();
    let booking = { ...oldBooking, ...values };
    Session.setBooking(booking);
  }

  static updateConfig(values: Partial<be.GlobalConfig>): void {
    let oldConfig = Session.getConfig();
    let config = { ...oldConfig, ...values };
    Session.setConfig(config);
  }

  static updateCurrentLeg(values: Partial<be.Reservation>): void {
    Session.updateLeg(window.legIndex, values);
  }

  static updateFrpPurchase(values: Partial<be.FrequentRiderProgramSessionData>): void {
    const oldPurchase = Session.get("frpPurchase");
    const purchase = { ...oldPurchase, ...values };
    Session.set("frpPurchase", purchase);
  }

  static updateLeg(legNum: number, values: Partial<be.Reservation>): void {
    let leg = Session.getLeg(legNum);
    leg = { ...leg, ...values };
    Session.setLeg(legNum, leg);
  }
}

export default Session;
